import { addDays } from 'date-fns';
import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { toIsoDay } from '../overviewDates';
import { buildPeopleRows } from './panoramaPeopleRows';
import type { PeriodRange } from './panoramaPeriod';

export const PENDING_STATUSES = ['PendingManager', 'PendingClient', 'PendingDP', 'PendingCorrection'];

export interface ClientDepartures {
	clientName: string;
	onVacation: number;
	scheduled: number;
	pending: number;
	total: number;
	teamSize: number;
}

export interface ClientDeparturesSnapshot {
	clients: ClientDepartures[];
}

export function buildClientDeparturesSnapshot(
	documents: VacationOverviewDocument[],
	referenceDate: Date,
	periodOrDays: PeriodRange | number
): ClientDeparturesSnapshot {
	let range: PeriodRange;

	if (typeof periodOrDays === 'object') {
		range = periodOrDays;
	} else {
		const from = toIsoDay(referenceDate);
		const to = toIsoDay(addDays(referenceDate, periodOrDays));
		range = { from, to };
	}

	const rows = buildPeopleRows(documents, referenceDate, range);
	const activeBase = documents.filter(d => d.active && d.inBase);

	const clientMap = new Map<string, ClientDepartures>();
	const clientTeamSize = new Map<string, Set<string>>();

	activeBase.forEach(doc => {
		const clients = doc.clientNames.length > 0 ? doc.clientNames : [];
		if (clients.length === 0) return;

		clients.forEach(clientName => {
			if (!clientTeamSize.has(clientName)) {
				clientTeamSize.set(clientName, new Set());
			}
			clientTeamSize.get(clientName)!.add(doc.collaboratorId);
		});
	});

	rows.forEach(row => {
		const relevantStatuses = ['on-vacation', 'scheduled', 'pending', 'approval-urgent'];
		if (!relevantStatuses.includes(row.status)) return;

		row.clientNames.forEach(clientName => {
			if (!clientMap.has(clientName)) {
				clientMap.set(clientName, {
					clientName,
					onVacation: 0,
					scheduled: 0,
					pending: 0,
					total: 0,
					teamSize: 0,
				});
			}

			const entry = clientMap.get(clientName)!;
			if (row.status === 'on-vacation') {
				entry.onVacation++;
			} else if (row.status === 'scheduled') {
				entry.scheduled++;
			} else if (row.status === 'pending' || row.status === 'approval-urgent') {
				entry.pending++;
			}
			entry.total++;
		});
	});

	const clients = Array.from(clientMap.values())
		.map(c => ({
			...c,
			teamSize: clientTeamSize.get(c.clientName)?.size ?? 0,
		}))
		.sort((a, b) => {
			if (b.total !== a.total) return b.total - a.total;
			return a.clientName.localeCompare(b.clientName);
		})
		.slice(0, 10);

	return { clients };
}
